import { Button, Box, FormTextField, HookForm, Stack, TabsField, Tab } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useAuthMutation } from '../../../api/auth';
import { useCallback, useEffect, useState } from 'react';
import { ApiAuthChallengeRequired } from '../../../openapi';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useForm } from 'react-hook-form';
import { useFormError } from '../../../hooks/useFormError';
import { AuthChallenge } from './AuthChallenge';
import { AuthHeader } from './AuthHeader';

type FormValues = {
  username: string;
  password: string;
  otp: string;
  newPassword: string;
  firstFactor: 'email' | 'phone';
};

function isEmail(value: string) {
  return value.includes('@');
}

function parseUsername(username: string) {
  if (isEmail(username)) {
    return { email: username.replace(/\s+/g, '') };
  }

  return { phone: '+' + username.replace(/\D/g, '') };
}

export function AuthForm({ signup }: { signup: boolean }) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const authMutation = useAuthMutation({ idempotencyKey });

  const form = useForm<FormValues>({
    defaultValues: {
      username: '',
      password: '',
      otp: '',
      newPassword: '',
      firstFactor: 'email',
    },
  });

  const [currentChallenge, setCurrentChallenge] = useState<ApiAuthChallengeRequired | undefined>();

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (currentChallenge) {
        switch (currentChallenge.challenge) {
          case 'PASSWORD':
            authMutation.mutate({
              questId: currentChallenge.questId,
              solution: values.password,
            });
            break;
          case 'SET_NEW_PASSWORD':
            authMutation.mutate({
              questId: currentChallenge.questId,
              solution: values.newPassword,
            });
            break;
          default:
            if (currentChallenge?.otp) {
              authMutation.mutate({
                questId: currentChallenge.questId,
                solution: values.otp,
              });
            }
            break;
        }
      } else {
        const parsed = parseUsername(values.username);
        authMutation.mutate(parsed);
        form.setValue('firstFactor', parsed.email ? 'email' : 'phone');
      }
    },
    [authMutation, currentChallenge, form],
  );

  useEffect(
    function cacheRequiredChallenge() {
      if (authMutation.data?.type === 'CHALLENGE_REQUIRED') {
        setCurrentChallenge(authMutation.data);
      }
    },
    [authMutation.data],
  );

  const handleReset = useCallback(() => {
    form.reset();
    authMutation.reset();
    setCurrentChallenge(undefined);
  }, [form, authMutation]);

  useEffect(() => {
    // reset dirty/validation state since multistep form uses the same mutation
    if (authMutation.isSuccess) {
      form.reset(undefined, {
        keepValues: true,
      });
    }
  }, [form, authMutation.isSuccess]);

  useFormError(
    form,
    currentChallenge?.otp
      ? 'otp'
      : currentChallenge?.challenge === 'PASSWORD'
        ? 'password'
        : 'username',
    authMutation.error,
  );

  const isUsernameHidden = Boolean(currentChallenge);

  const firstFactor = form.watch('firstFactor');
  const usernameLabel = firstFactor === 'phone' ? t('Phone number') : t('Email');

  return (
    <Stack
      onSubmit={handleSubmit}
      component={HookForm}
      form={form}
      spacing={4}
      width="100%"
      maxWidth={440}
      padding={4}
    >
      <AuthHeader
        currentChallenge={currentChallenge}
        signup={signup}
        usernameLabel={usernameLabel}
      />
      {signup || isUsernameHidden ? null : (
        <TabsField name="firstFactor" label={t('Login with')}>
          <Tab label={t('Email')} value="email" />
          <Tab label={t('Phone')} value="phone" />
        </TabsField>
      )}

      <FormTextField
        key={isUsernameHidden ? 'username-hidden' : 'username'}
        id="username"
        name="username"
        label={usernameLabel}
        fullWidth
        required
        inputMode={firstFactor === 'phone' ? 'tel' : 'email'}
        autoComplete={firstFactor === 'phone' ? 'tel' : 'email'}
        autoFocus={!isUsernameHidden}
        disabled={isUsernameHidden}
        sx={isUsernameHidden ? { display: 'none' } : undefined}
      />

      {currentChallenge ? (
        <AuthChallenge challenge={currentChallenge} authMutation={authMutation} />
      ) : null}
      {currentChallenge ? (
        <Box>
          <Button type="reset" variant="text" color="info" onClick={handleReset} tabIndex={2}>
            {t('Change {{usernameLabel}}', { usernameLabel: usernameLabel.toLowerCase() })}
          </Button>
        </Box>
      ) : null}
      <Button fullWidth size="large" type="submit" loading={authMutation.isPending} color="primary">
        {t('Continue')}
      </Button>
    </Stack>
  );
}
